import React, {useState, useEffect, useRef, useCallback, memo} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import TextEllipsis from '../../../components/TextEllipsis/TextEllipsis';
import {CYCLE_DURATION} from '../constants';

const MIN_PAGE_INTERVAL = 5000; // Minimum 5 seconds per page
const PROGRESS_UPDATE_INTERVAL = 500; // Update progress every 500ms

// Isolated progress indicator - updates independently without causing Widget re-renders
const PaginationProgress = memo(({currentPage, totalPages, pageInterval}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min((elapsed / pageInterval) * 100, 100));
    }, PROGRESS_UPDATE_INTERVAL);
    return () => clearInterval(timer);
  }, [currentPage, pageInterval]);

  return (
    <PageIndicatorContainer>
      <PageIndicator>page {currentPage + 1} of {totalPages}</PageIndicator>
      <ProgressBar>
        <ProgressFill $progress={progress} />
      </ProgressBar>
    </PageIndicatorContainer>
  );
});

const Widget = ({title, widgetKey, columns, data, loading, pathField, pathField2, onMaximize, fileMakerURL, qcOnDemandBaseUrl}) => {
  const contentRef = useRef(null);
  const theadRef = useRef(null);
  const tbodyRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageBreaks, setPageBreaks] = useState(null);
  const [isCalculating, setIsCalculating] = useState(true);
  const [activePathFieldIndex, setActivePathFieldIndex] = useState(0);
  const [popupRow, setPopupRow] = useState(null);

  // Measure each row's actual height and build variable-sized page boundaries
  const calculatePageBreaks = useCallback(() => {
    if (!contentRef.current || !theadRef.current || !tbodyRef.current || data.length === 0) {
      setPageBreaks(null);
      setIsCalculating(false);
      return;
    }

    const containerHeight = contentRef.current.clientHeight;
    const theadHeight = theadRef.current.offsetHeight;
    const availableHeight = containerHeight - theadHeight;

    const rows = tbodyRef.current.querySelectorAll('tr');
    if (rows.length === 0) {
      setPageBreaks(null);
      setIsCalculating(false);
      return;
    }

    // Greedily fill pages using actual row heights
    const breaks = [0];
    let usedHeight = 0;

    for (let i = 0; i < rows.length; i++) {
      const rowHeight = rows[i].offsetHeight;

      if (usedHeight + rowHeight > availableHeight && i > breaks[breaks.length - 1]) {
        // Start a new page at this row
        breaks.push(i);
        usedHeight = rowHeight;
      } else {
        usedHeight += rowHeight;
      }
    }

    setPageBreaks(breaks);
    setIsCalculating(false);
  }, [data]);

  // Trigger calculation after render
  useEffect(() => {
    setIsCalculating(true);
    const timer = setTimeout(calculatePageBreaks, 50);
    return () => clearTimeout(timer);
  }, [data, calculatePageBreaks]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      setTimeout(calculatePageBreaks, 50);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculatePageBreaks]);

  // Calculate pagination
  const totalPages = pageBreaks ? pageBreaks.length : 1;
  const needsPagination = totalPages > 1;

  // Auto-cycle through pages (progress bar is handled by isolated PaginationProgress component)
  const pageInterval = Math.max(CYCLE_DURATION / totalPages, MIN_PAGE_INTERVAL);

  useEffect(() => {
    if (!needsPagination || loading || isCalculating) {
      setCurrentPage(0);
      return;
    }

    const pageTimer = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
    }, pageInterval);

    return () => clearInterval(pageTimer);
  }, [needsPagination, totalPages, pageInterval, loading, isCalculating]);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(0);
  }, [data]);

  // Reset path field index when page changes
  useEffect(() => {
    setActivePathFieldIndex(0);
  }, [currentPage]);

  // Toggle between pathField and pathField2 at half the page interval
  useEffect(() => {
    if (!pathField2 || loading || isCalculating) {
      setActivePathFieldIndex(0);
      return;
    }

    const pageInterval = needsPagination
      ? Math.max(CYCLE_DURATION / totalPages, MIN_PAGE_INTERVAL)
      : CYCLE_DURATION;
    const halfInterval = pageInterval / 2;

    // Toggle between path fields continuously
    const toggleTimer = setInterval(() => {
      setActivePathFieldIndex(prev => (prev === 0 ? 1 : 0));
    }, halfInterval);

    return () => clearInterval(toggleTimer);
  }, [pathField2, loading, isCalculating, needsPagination, totalPages]); // Removed currentPage - separate effect handles reset

  // Get current page data using variable page boundaries
  const safePage = pageBreaks ? Math.min(currentPage, totalPages - 1) : 0;
  const displayData = pageBreaks
    ? data.slice(pageBreaks[safePage] || 0, pageBreaks[safePage + 1] || data.length)
    : [];

  // Get the currently active path field based on toggle state
  const getActivePathField = () => {
    if (!pathField2) return pathField;
    return activePathFieldIndex === 0 ? pathField : pathField2;
  };
  const activePathField = getActivePathField();

  // Handle maximize button click - call parent callback
  const handleMaximize = (e) => {
    e.stopPropagation();
    if (onMaximize) {
      onMaximize(widgetKey);
    }
  };

  const navigateToOption = (option, row) => {
    if (option === 'qod' && qcOnDemandBaseUrl && row.wo_memfis) {
      window.open(`${qcOnDemandBaseUrl}/requests/wo/${row.wo_memfis}`, '_blank');
    } else if (option === 'memfis' && fileMakerURL && row.wo_memfis) {
      window.open(fileMakerURL.replace('@MEMFIS_WO@', row.wo_memfis), '_blank');
    } else if (option === 'action' && row.action_id) {
      window.open(`/storage?action_id=${row.action_id}`, '_blank');
    }
  };

  // Handle row click - show popup with navigation options, or navigate directly if only one option
  const handleRowClick = (e, row) => {
    if (!row.action_id && !row.wo_memfis) return;
    e.stopPropagation();

    const options = [];
    if (qcOnDemandBaseUrl && row.wo_memfis) options.push('qod');
    if (fileMakerURL && row.wo_memfis) options.push('memfis');
    if (row.action_id) options.push('action');

    if (options.length === 1) {
      navigateToOption(options[0], row);
    } else if (options.length > 1) {
      setPopupRow(row);
    }
  };

  const handlePopupOptionClick = (option) => {
    if (!popupRow) return;
    navigateToOption(option, popupRow);
    setPopupRow(null);
  };

  const closePopup = () => setPopupRow(null);

  // Calculate column widths - use custom width if specified, otherwise distribute remaining space equally
  const getColumnWidth = (col) => {
    if (col.width) {
      return col.width;
    }
    const fixedWidthSum = columns.reduce((sum, c) => sum + (c.width || 0), 0);
    const flexColumns = columns.filter(c => !c.width).length;
    return flexColumns > 0 ? (100 - fixedWidthSum) / flexColumns : 0;
  };

  // Get urgency color based on urgency level
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 0: return '#dc3545'; // 🔴 Overdue
      case 1: return '#fd7e14'; // 🟠 < 6h
      case 2: return '#ffc107'; // 🟡 < 24h
      default: return null;
    }
  };

  return (
    <WidgetStyled className="widget">
      <WidgetHeader>
        <HeaderLeft>
          <span>{title}</span>
          <MaximizeButton onClick={handleMaximize} title="Maximize">
            <i className="fa fa-expand" />
          </MaximizeButton>
        </HeaderLeft>
        {needsPagination && !isCalculating && (
          <PaginationProgress
            currentPage={safePage}
            totalPages={totalPages}
            pageInterval={pageInterval}
          />
        )}
      </WidgetHeader>
      <WidgetContent ref={contentRef}>
        {loading ? (
          <LoadingOverlay>
            <i className="fa fa-spinner fa-spin" />
          </LoadingOverlay>
        ) : null}
        <WidgetTable>
          <thead ref={theadRef}>
            <tr>
              <th>
                <RowColumns>
                  {columns.map(col => (
                    <RowCell key={col.key} $width={getColumnWidth(col)} $maxWidth={col.maxWidth} $flex={!col.width}>{col.label}</RowCell>
                  ))}
                </RowColumns>
              </th>
            </tr>
          </thead>
          {isCalculating && (
            <MeasureTbody ref={tbodyRef}>
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <RowContainer>
                      <RowColumns>
                        {columns.map(col => (
                          <RowCell key={col.key} $width={getColumnWidth(col)} $maxWidth={col.maxWidth} $flex={!col.width}>
                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                          </RowCell>
                        ))}
                      </RowColumns>
                      {pathField && row[pathField] && (
                        <RowPath>
                          <div>{row[pathField]}</div>
                        </RowPath>
                      )}
                    </RowContainer>
                  </td>
                </tr>
              ))}
            </MeasureTbody>
          )}
          {!isCalculating && (
            <tbody>
              {displayData.length === 0 ? (
                <tr>
                  <td className="empty-row">
                    No data available
                  </td>
                </tr>
              ) : (
                displayData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    className={(row.action_id || row.wo_memfis) ? 'clickable' : ''}
                    title={(row.action_id || row.wo_memfis) ? 'Click to view WO or action details' : ''}
                    onClick={(e) => handleRowClick(e, row)}
                  >
                    <td>
                      <RowContainer>
                        <RowColumns>
                          {columns.map(col => (
                            <RowCell
                              key={col.key}
                              title={col.title}
                              $width={getColumnWidth(col)}
                              $maxWidth={col.maxWidth}
                              $flex={!col.width}
                              $bgColor={col.key === 'wo_memfis' ? getUrgencyColor(row.urgency) : null}
                            >
                              {col.render ? col.render(row[col.key], row) : row[col.key]}
                            </RowCell>
                          ))}
                        </RowColumns>
                        {activePathField && row[activePathField] && (
                          <RowPath>
                            <TextEllipsis
                              data={row[activePathField]}
                              useMonospaceFont={true}
                              noTooltip={true}
                            />
                          </RowPath>
                        )}
                      </RowContainer>
                    </td>
                  </TableRow>
                ))
              )}
            </tbody>
          )}
        </WidgetTable>
      </WidgetContent>
      {popupRow && (
        <PopupOverlay onClick={closePopup}>
          <PopupMenu onClick={e => e.stopPropagation()}>
            <PopupTitle>What do you want to open?</PopupTitle>
            {qcOnDemandBaseUrl && popupRow.wo_memfis && (
              <PopupOption onClick={() => handlePopupOptionClick('qod')}>WO in QOD</PopupOption>
            )}
            {fileMakerURL && popupRow.wo_memfis && (
              <PopupOption onClick={() => handlePopupOptionClick('memfis')}>WO in MEMFIS</PopupOption>
            )}
            {popupRow.action_id && (
              <PopupOption onClick={() => handlePopupOptionClick('action')}>MAM Action</PopupOption>
            )}
          </PopupMenu>
        </PopupOverlay>
      )}
    </WidgetStyled>
  );
};

Widget.propTypes = {
  title: PropTypes.string.isRequired,
  widgetKey: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    width: PropTypes.number, // Optional: percentage width (e.g., 25 for 25%). Columns without width share remaining space equally.
    maxWidth: PropTypes.number // Optional: max width in pixels. Caps column expansion on wide containers; surplus space goes to flex columns.
  })).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  pathField: PropTypes.string,
  pathField2: PropTypes.string, // Optional: second path field that alternates with pathField during page display
  onMaximize: PropTypes.func, // Callback when maximize button is clicked, receives snapshot of data
  completedCheck: PropTypes.func, // Optional: (row) => boolean. Pages containing matching rows cycle faster
  fileMakerURL: PropTypes.string,
  qcOnDemandBaseUrl: PropTypes.string
};

Widget.defaultProps = {
  loading: false
};

const WidgetStyled = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const WidgetHeader = styled.div`
  background: #343a40;
  color: #fff;
  font-size: 1.4rem;
  font-weight: bold;
  padding: 7px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MaximizeButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 0.9rem;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const PageIndicatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const PageIndicator = styled.span`
  font-size: 1rem;
  font-weight: normal;
  opacity: 0.8;
`;

const ProgressBar = styled.div`
  width: 120px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$progress}%;
  background: rgba(255, 255, 255, 0.8);
  transition: width 0.5s linear;
`;

const WidgetContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  i {
    font-size: 2rem;
    color: #343a40;
  }
`;

const WidgetTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: #f8f9fa;

    th {
      border-bottom: 2px solid #dee2e6;
      font-weight: bold;
      text-align: left;
      white-space: normal;
      word-wrap: break-word;
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: #f8f9fa;
      }

      &:not(:last-child) td {
        border-bottom: 1px solid #dee2e6;
      }

      &.clickable {
        cursor: pointer;

        &:hover {
          background: #e2e6ea;
        }
      }
    }

    td {
      padding: 0;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;

      &.empty-row {
        text-align: center;
        color: #6c757d;
        padding: 24px;
      }
    }
  }
`;

const TableRow = styled.tr``;

const MeasureTbody = styled.tbody`
  visibility: hidden;
  width: 100%;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowColumns = styled.div`
  display: flex;
  padding: 5px 0;
`;

const RowCell = styled.div`
  flex: ${props => props.$flex ? '1 1' : '0 0'} ${props => props.$width}%;
  width: ${props => props.$width}%;
  ${props => props.$maxWidth ? `max-width: ${props.$maxWidth}px;` : ''}
  box-sizing: border-box;
  padding: 0 7px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  ${props => props.$bgColor ? `
    background-color: ${props.$bgColor};
    color: #fff;
    font-weight: bold;
  ` : ''}
`;

const RowPath = styled.div`
  padding: 0 12px 2px 12px;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  white-space: nowrap;
  font-size: 0.85em;
  color: #6c757d;
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupMenu = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  min-width: 250px;
  padding: 8px 0;
`;

const PopupTitle = styled.div`
  padding: 8px 16px;
  font-size: 1.5rem;
  color: #6c757d;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 4px;
`;

const PopupOption = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 1.3rem;
  color: #212529;

  &:hover {
    background: #f0f0f0;
  }
`;

export default memo(Widget);
